import { useState, useEffect, useCallback } from 'react';
import { FileText, Settings, Zap, TestTube, Trash2, CheckCircle2, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../../api/client';

interface NfseStatus {
  provider: string;
  ambiente: string;
  temChaves: boolean;
  cnpjEmissor: string | null;
  codigoMunicipio: string | null;
}

const PROVIDER_INFO: Record<string, { label: string; site: string }> = {
  simulado: { label: 'Simulado (interno)', site: '' },
  focus_nfe: { label: 'Focus NFe', site: 'https://focusnfe.com.br' },
  plugnotas: { label: 'PlugNotas', site: 'https://plugnotas.com.br' },
};

const AMBIENTE_CONFIG: Record<string, { bg: string; color: string; icon: string; label: string }> = {
  simulado: { bg: '#dbeafe', color: '#1e40af', icon: '🔵', label: 'SIMULADO' },
  homologacao: { bg: '#fef3c7', color: '#92400e', icon: '🟡', label: 'HOMOLOGAÇÃO' },
  producao: { bg: '#dcfce7', color: '#166534', icon: '🟢', label: 'PRODUÇÃO' },
};

export function NfseConfigPanel() {
  const [status, setStatus] = useState<NfseStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [simulating, setSimulating] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [testResult, setTestResult] = useState<any>(null);
  const [simResult, setSimResult] = useState<any>(null);
  const [form, setForm] = useState({
    provider: 'focus_nfe',
    apiKey: '',
    ambiente: 'homologacao',
    cnpjEmissor: '',
    inscricaoMunicipal: '',
    codigoMunicipio: '',
  });

  const loadStatus = useCallback(async () => {
    try {
      const { data } = await api.get('/billing/nfse/status');
      setStatus(data);
    } catch { /* silent */ }
    setLoading(false);
  }, []);

  useEffect(() => { loadStatus(); }, [loadStatus]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const { data } = await api.post('/billing/nfse/config', form);
      toast.success(`Config salva! Provider: ${data.provider}, Ambiente: ${data.ambiente}`);
      setShowForm(false);
      loadStatus();
    } catch { toast.error('Erro de conexão'); }
    setSaving(false);
  };

  const handleClear = async () => {
    if (!confirm('Remover configuração NFS-e? O sistema voltará ao modo simulado.')) return;
    try {
      await api.delete('/billing/nfse/config');
      toast.success('Config removida. Modo simulado ativo.');
      loadStatus();
      setTestResult(null);
      setSimResult(null);
    } catch { toast.error('Erro ao limpar'); }
  };

  const handleTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const { data } = await api.post('/billing/nfse/testar');
      setTestResult(data);
      data.ok ? toast.success(data.mensagem) : toast.error(data.mensagem);
    } catch { toast.error('Erro ao testar'); }
    setTesting(false);
  };

  const handleSimulate = async () => {
    setSimulating(true);
    setSimResult(null);
    try {
      const { data } = await api.post('/billing/nfse/simular');
      setSimResult(data);
      toast.success(data.mensagem || 'Simulação concluída');
    } catch { toast.error('Erro na simulação'); }
    setSimulating(false);
  };

  if (loading) return null;

  const ambCfg = AMBIENTE_CONFIG[status?.ambiente || 'simulado'];
  const provInfo = PROVIDER_INFO[status?.provider || 'simulado'];

  return (
    <div style={{
      background: 'var(--bg-card, white)', borderRadius: 14,
      border: '1px solid var(--border-color, #e5e7eb)', padding: 20, marginTop: 20,
    }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <h3 style={{ fontSize: '1rem', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8, margin: 0 }}>
          <FileText size={18} style={{ color: '#059669' }} /> Configuração NFS-e
        </h3>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <span style={{ fontSize: '0.68rem', color: '#9ca3af' }}>{provInfo.label}</span>
          <span style={{
            padding: '4px 14px', borderRadius: 20, fontSize: '0.72rem', fontWeight: 700,
            background: ambCfg.bg, color: ambCfg.color, letterSpacing: '0.5px',
          }}>
            {ambCfg.icon} {ambCfg.label}
          </span>
        </div>
      </div>

      {/* Status info */}
      <div style={{
        background: ambCfg.bg, borderRadius: 10, padding: '14px 18px', marginBottom: 16,
        border: `1px solid ${ambCfg.color}22`,
      }}>
        {status?.ambiente === 'simulado' ? (
          <div>
            <div style={{ fontSize: '0.85rem', fontWeight: 600, color: ambCfg.color, marginBottom: 4 }}>
              Modo Simulado Ativo
            </div>
            <p style={{ fontSize: '0.78rem', color: ambCfg.color, opacity: 0.8, margin: 0 }}>
              Nenhum provider configurado. Emissões serão simuladas internamente com dados compatíveis
              com o padrão NFS-e. Configure um provider (Focus NFe, PlugNotas) para emissão real.
            </p>
          </div>
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div>
              <div style={{ fontSize: '0.85rem', fontWeight: 600, color: ambCfg.color, marginBottom: 4 }}>
                {provInfo.label} — {status?.ambiente === 'homologacao' ? 'Homologação' : 'Produção'}
              </div>
              <div style={{ fontSize: '0.72rem', color: ambCfg.color, opacity: 0.7 }}>
                {status?.cnpjEmissor && `CNPJ: ${status.cnpjEmissor}`}
                {status?.codigoMunicipio && ` · Município: ${status.codigoMunicipio}`}
              </div>
            </div>
            <button onClick={handleClear} style={{
              display: 'flex', alignItems: 'center', gap: 4, padding: '6px 12px', borderRadius: 8,
              border: '1px solid #fecaca', background: '#fef2f2', color: '#dc2626',
              fontSize: '0.72rem', fontWeight: 600, cursor: 'pointer',
            }}>
              <Trash2 size={12} /> Remover
            </button>
          </div>
        )}
      </div>

      {/* Ações */}
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 16 }}>
        <button onClick={() => setShowForm(!showForm)} style={{
          display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px', borderRadius: 10,
          border: '1px solid var(--border-color, #e5e7eb)', background: 'var(--bg-card, white)',
          fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer', color: 'var(--text-main, #1e293b)',
        }}>
          <Settings size={14} /> {showForm ? 'Fechar' : 'Configurar'}
        </button>

        <button onClick={handleTest} disabled={testing} style={{
          display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px', borderRadius: 10,
          border: 'none', background: '#059669', color: 'white',
          fontSize: '0.78rem', fontWeight: 600, cursor: testing ? 'wait' : 'pointer',
          opacity: testing ? 0.7 : 1,
        }}>
          <Zap size={14} /> {testing ? 'Testando...' : 'Testar'}
        </button>

        <button onClick={handleSimulate} disabled={simulating} style={{
          display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px', borderRadius: 10,
          border: 'none', background: '#0891b2', color: 'white',
          fontSize: '0.78rem', fontWeight: 600, cursor: simulating ? 'wait' : 'pointer',
          opacity: simulating ? 0.7 : 1,
        }}>
          <TestTube size={14} /> {simulating ? 'Emitindo...' : 'Simular Emissão'}
        </button>
      </div>

      {/* Form de configuração */}
      {showForm && (
        <div style={{
          background: 'var(--bg-main, #f9fafb)', borderRadius: 10, padding: 18,
          border: '1px solid var(--border-color, #e5e7eb)', marginBottom: 16,
        }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label style={{ fontSize: '0.72rem', fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', marginBottom: 4, display: 'block' }}>
                Provider
              </label>
              <select value={form.provider} onChange={e => setForm({ ...form, provider: e.target.value })} style={{
                width: '100%', padding: '10px 14px', borderRadius: 8, border: '1px solid var(--border-color, #e5e7eb)',
                background: 'var(--bg-card, white)', color: 'var(--text-main, #1e293b)', fontSize: '0.85rem',
              }}>
                <option value="focus_nfe">Focus NFe</option>
                <option value="plugnotas">PlugNotas</option>
              </select>
            </div>
            <div>
              <label style={{ fontSize: '0.72rem', fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', marginBottom: 4, display: 'block' }}>
                Ambiente
              </label>
              <select value={form.ambiente} onChange={e => setForm({ ...form, ambiente: e.target.value })} style={{
                width: '100%', padding: '10px 14px', borderRadius: 8, border: '1px solid var(--border-color, #e5e7eb)',
                background: 'var(--bg-card, white)', color: 'var(--text-main, #1e293b)', fontSize: '0.85rem',
              }}>
                <option value="homologacao">Homologação</option>
                <option value="producao">Produção</option>
              </select>
            </div>
            <div style={{ gridColumn: '1 / -1' }}>
              <label style={{ fontSize: '0.72rem', fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', marginBottom: 4, display: 'block' }}>
                API Key *
              </label>
              <input type="password" placeholder="Token do provider..." value={form.apiKey}
                onChange={e => setForm({ ...form, apiKey: e.target.value })}
                style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: '1px solid var(--border-color, #e5e7eb)', background: 'var(--bg-card, white)', color: 'var(--text-main, #1e293b)', fontSize: '0.85rem', fontFamily: 'monospace' }}
              />
            </div>
            <div>
              <label style={{ fontSize: '0.72rem', fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', marginBottom: 4, display: 'block' }}>
                CNPJ Emissor
              </label>
              <input type="text" placeholder="00.000.000/0000-00" value={form.cnpjEmissor}
                onChange={e => setForm({ ...form, cnpjEmissor: e.target.value.replace(/\D/g, '') })}
                style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: '1px solid var(--border-color, #e5e7eb)', background: 'var(--bg-card, white)', color: 'var(--text-main, #1e293b)', fontSize: '0.85rem' }}
              />
            </div>
            <div>
              <label style={{ fontSize: '0.72rem', fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', marginBottom: 4, display: 'block' }}>
                Inscrição Municipal
              </label>
              <input type="text" placeholder="123456" value={form.inscricaoMunicipal}
                onChange={e => setForm({ ...form, inscricaoMunicipal: e.target.value })}
                style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: '1px solid var(--border-color, #e5e7eb)', background: 'var(--bg-card, white)', color: 'var(--text-main, #1e293b)', fontSize: '0.85rem' }}
              />
            </div>
            <div>
              <label style={{ fontSize: '0.72rem', fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', marginBottom: 4, display: 'block' }}>
                Código Município (IBGE)
              </label>
              <input type="text" placeholder="3550308 (São Paulo)" value={form.codigoMunicipio}
                onChange={e => setForm({ ...form, codigoMunicipio: e.target.value })}
                style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: '1px solid var(--border-color, #e5e7eb)', background: 'var(--bg-card, white)', color: 'var(--text-main, #1e293b)', fontSize: '0.85rem' }}
              />
            </div>
          </div>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 14 }}>
            <button onClick={() => setShowForm(false)} style={{
              padding: '8px 20px', borderRadius: 8, border: '1px solid var(--border-color, #e5e7eb)',
              background: 'var(--bg-card, white)', fontSize: '0.78rem', cursor: 'pointer',
            }}>Cancelar</button>
            <button onClick={handleSave} disabled={saving} style={{
              padding: '8px 20px', borderRadius: 8, border: 'none', background: '#059669',
              color: 'white', fontWeight: 700, fontSize: '0.78rem', cursor: saving ? 'wait' : 'pointer',
            }}>{saving ? 'Salvando...' : 'Salvar'}</button>
          </div>
        </div>
      )}

      {/* Resultado Teste */}
      {testResult && (
        <div style={{
          background: testResult.ok ? '#f0fdf4' : '#fef2f2', borderRadius: 10,
          padding: '12px 16px', marginBottom: 12,
          border: `1px solid ${testResult.ok ? '#bbf7d0' : '#fecaca'}`,
          display: 'flex', alignItems: 'center', gap: 10,
        }}>
          {testResult.ok ? <CheckCircle2 size={16} color="#16a34a" /> : <AlertTriangle size={16} color="#dc2626" />}
          <div>
            <div style={{ fontSize: '0.82rem', fontWeight: 600, color: testResult.ok ? '#166534' : '#991b1b' }}>
              {testResult.mensagem}
            </div>
            <div style={{ fontSize: '0.68rem', color: testResult.ok ? '#16a34a' : '#dc2626', textTransform: 'uppercase' }}>
              Provider: {testResult.provider} · Ambiente: {testResult.ambiente}
            </div>
          </div>
        </div>
      )}

      {/* Resultado Simulação */}
      {simResult && (
        <div style={{
          background: '#ecfdf5', borderRadius: 10, padding: '14px 18px',
          border: '1px solid #a7f3d0',
        }}>
          <div style={{ fontSize: '0.78rem', fontWeight: 700, color: '#065f46', marginBottom: 8 }}>
            Resultado da Emissão ({simResult.resultado?.provider || 'simulado'})
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, fontSize: '0.75rem' }}>
            <div>
              <div style={{ fontWeight: 600, color: '#1e293b', marginBottom: 2 }}>NFS-e</div>
              <div style={{ fontFamily: 'monospace', fontSize: '0.68rem', color: '#6b7280' }}>
                Nº: {simResult.resultado?.numero || '—'}<br />
                ID: {simResult.resultado?.id}
              </div>
            </div>
            <div>
              <div style={{ fontWeight: 600, color: '#1e293b', marginBottom: 2 }}>Valor</div>
              <div style={{ fontFamily: 'monospace', fontSize: '0.68rem', color: '#6b7280' }}>
                R$ {simResult.resultado?.valorTotal?.toFixed(2) || '—'}<br />
                Status: {simResult.resultado?.status}
              </div>
            </div>
            <div>
              <div style={{ fontWeight: 600, color: '#1e293b', marginBottom: 2 }}>Verificação</div>
              <div style={{ fontFamily: 'monospace', fontSize: '0.68rem', color: '#6b7280' }}>
                Código: {simResult.resultado?.codigoVerificacao || '—'}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
